import { DocumentSignFilterableStatus, DocumentSignStatus } from "./DocumentSign.dto";
import { LoLang } from "../common/common.types";

export function ReadableDocumentSignStatus(status: DocumentSignStatus, lang?: LoLang | undefined, defaultVal = "-"): string {
  switch (status) {
    case DocumentSignStatus.Pending:
    case DocumentSignStatus.Creating:
      return lang === "KO" ? "서명 생성 중" : "Creating Sign";
    case DocumentSignStatus.CreatingFailed:
      return lang === "KO" ? "서명 생성 실패" : "Creating Failed";
    case DocumentSignStatus.None:
    case DocumentSignStatus.Wait:
    case DocumentSignStatus.Delivering:
    case DocumentSignStatus.DeliveringFailed:
    case DocumentSignStatus.Delivered:
      return lang === "KO" ? "서명 대기" : "Wait for Signing";
    case DocumentSignStatus.Signing:
      return lang === "KO" ? "서명 진행 중" : "Signing in Progress";
    case DocumentSignStatus.SignerDeliveringFailed:
      return lang === "KO" ? "메일 전송 실패" : "Failed Delivery Document";
    case DocumentSignStatus.Signed:
    case DocumentSignStatus.Finalizing:
    case DocumentSignStatus.FinalizingFailed:
    case DocumentSignStatus.Finalized:
      return lang === "KO" ? "서명 완료" : "Signing Completed";
    case DocumentSignStatus.Decline:
    case DocumentSignStatus.Declining:
    case DocumentSignStatus.DecliningFailed:
    case DocumentSignStatus.Declined:
      return lang === "KO" ? "서명 거부" : "Signing Declined";
    case DocumentSignStatus.Expiring:
    case DocumentSignStatus.ExpiringFailed:
    case DocumentSignStatus.Expired:
      return lang === "KO" ? "기한 만료" : "Signing Expired";
    case DocumentSignStatus.Cancel:
    case DocumentSignStatus.Canceling:
    case DocumentSignStatus.CancelingFailed:
    case DocumentSignStatus.Canceled:
      return lang === "KO" ? "서명 취소" : "Signing Canceled";
    default:
      return status || defaultVal;
  }
}

/**
 * subejct list 등록을 마치고 메일 전송을 하지 않은 상태 === '등록 완료'
 * 이때 subject는 document를 가지고 있지 않아 status가 undefined가 됨.
 * 단, 요청에 status를 포함해야할 때 undefined를 사용하게 되면 아무 status도 지정하지 않은 상태와 구분할 수 없어서 메일 전송 task에서 가장 첫번째로 부여하는 상태인 NONE을 빌려옴.
 */
export function readableDocumentSignStatusForEConsent(status?: DocumentSignStatus, lang?: LoLang | undefined, defaultVal = "-"): string {
  if (!status || status === DocumentSignStatus.None) {
    return lang === "KO" ? "등록 완료" : "Subject Added";
  }

  return ReadableDocumentSignStatus(status, lang, defaultVal);
}

export function readableDocumentSignFilterableStatus(filterableStatus?: DocumentSignFilterableStatus, lang?: string): string {
  switch (filterableStatus) {
    case DocumentSignFilterableStatus.Registered:
      return lang === "KO" ? "등록 완료" : "Subject Added";
    case DocumentSignFilterableStatus.WaitForSigning:
      return lang === "KO" ? "서명 대기" : "Wait for Signing";
    case DocumentSignFilterableStatus.SigningInProgress:
      return lang === "KO" ? "서명 진행 중" : "Signing in Progress";
    case DocumentSignFilterableStatus.FailedDeliveryDocument:
      return lang === "KO" ? "메일 전송 실패" : "Failed Delivery Document";
    case DocumentSignFilterableStatus.SigningCompleted:
      return lang === "KO" ? "서명 완료" : "Signing Completed";
    case DocumentSignFilterableStatus.SigningDeclined:
      return lang === "KO" ? "서명 거부" : "Signing Declined";
    case DocumentSignFilterableStatus.SigningExpired:
      return lang === "KO" ? "기한 만료" : "Signing Expired";
    case DocumentSignFilterableStatus.SchedulingCompleted:
      return lang === "KO" ? "예약 발송 완료" : "Scheduling Completed";
    case DocumentSignFilterableStatus.Scheduling:
      return lang === "KO" ? "예약 발송 대기" : "Wait for Scheduling";
    case DocumentSignFilterableStatus.SchedulingCanceled:
      return lang === "KO" ? "예약 발송 취소" : "Scheduling Canceled";
    case DocumentSignFilterableStatus.SigningCanceled:
      return lang === "KO" ? "서명 취소" : "Signing Canceled";
    case DocumentSignFilterableStatus.Creating:
      return lang === "KO" ? "서명 생성 중" : "Creating Sign";
    case DocumentSignFilterableStatus.CreatingFailed:
      return lang === "KO" ? "서명 생성 실패" : "Creating Failed";
    default:
      return null;
  }
}

export function convertDocumentSignFilterableStatusToDocumentSignStatuses(
  filterableStatus: DocumentSignFilterableStatus
): DocumentSignStatus[] {
  switch (filterableStatus) {
    case DocumentSignFilterableStatus.Registered:
      return [DocumentSignStatus.None];
    case DocumentSignFilterableStatus.Creating:
      return [DocumentSignStatus.Pending, DocumentSignStatus.Creating];
    case DocumentSignFilterableStatus.CreatingFailed:
      return [DocumentSignStatus.CreatingFailed];
    case DocumentSignFilterableStatus.WaitForSigning:
      return [DocumentSignStatus.Wait, DocumentSignStatus.Delivering, DocumentSignStatus.DeliveringFailed, DocumentSignStatus.Delivered];
    case DocumentSignFilterableStatus.SigningInProgress:
      return [DocumentSignStatus.Signing];
    case DocumentSignFilterableStatus.FailedDeliveryDocument:
      return [DocumentSignStatus.SignerDeliveringFailed];
    case DocumentSignFilterableStatus.SigningCompleted:
      return [DocumentSignStatus.Signed, DocumentSignStatus.Finalizing, DocumentSignStatus.FinalizingFailed, DocumentSignStatus.Finalized];
    case DocumentSignFilterableStatus.SigningDeclined:
      return [DocumentSignStatus.Decline, DocumentSignStatus.Declining, DocumentSignStatus.DecliningFailed, DocumentSignStatus.Declined];
    case DocumentSignFilterableStatus.SigningExpired:
      return [DocumentSignStatus.Expiring, DocumentSignStatus.ExpiringFailed, DocumentSignStatus.Expired];
    case DocumentSignFilterableStatus.SigningCanceled:
      return [DocumentSignStatus.Cancel, DocumentSignStatus.Canceling, DocumentSignStatus.CancelingFailed, DocumentSignStatus.Canceled];
    default:
      return [];
  }
}

export function trimAndToLowerCase(value: string) {
  if (!value) {
    return "";
  }
  return value.trim().toLowerCase();
}
